import "./UseCaseContributePage.scss";

import { Breadcrumbs, CrumbProps } from "@amzn/stencil-react-components/breadcrumbs";
import { ButtonVariant } from "@amzn/stencil-react-components/button";
import { Col, Row } from "@amzn/stencil-react-components/layout";
import { MessageBannerType } from "@amzn/stencil-react-components/message-banner";
import { ProgressTracker, ProgressTrackerStep } from "@amzn/stencil-react-components/progress-tracker";
import { ProgressTrackerStepStatus } from "@amzn/stencil-react-components/progress-tracker";
import { IconCross } from "@amzn/stencil-react-icons";
import { Button } from "@amzn/uxm-stencil-react-components/lib/src/stencil/button";
import { useMessageBannersBox } from "common/components/MessageBannersBox/hook";
import { PageMainContainer } from "common/components/PageMainContainer/PageMainContainer";
import { getString } from "common/uistringlabels/uiStringUtils";
import { UserUseCaseLearnMoreLink, UserUseCaseStep } from "lib/models/userusecase-models";
import { UserPermissions } from "lib/services";
import { CatalogicalService } from "lib/services/catalogical-service";
import { CSRFService } from "lib/services/csrf-service";
import { UserInformation } from "lib/services/user-information-service";
import React, { FC, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";

import { AdditionalResourcesStep } from "./AdditionalResourcesStep";
import { AudienceAndImpactStep } from "./AudienceAndImpactStep";
import { BasicInformationStep } from "./BasicInformationStep";
import { buildUseCasePayload } from "./build-use-case-payload";
import { CONTRIBUTE_STEPS } from "./contribute-constants";
import { ReviewAndSubmitStep } from "./ReviewAndSubmitStep";
import { WorkflowStep } from "./WorkflowStep";

export interface UseCaseFormData {
  // Basic information
  title: string;
  shortDescription: string;
  toolsUsed: string[];
  categories: string[];

  // Workflow
  beforeWorkflow: string;
  afterWorkflow: string;
  steps: UserUseCaseStep[];

  // Audience and impact
  targetAudience: string[];
  difficultyLevel: string;
  impactIndicators: string[];
  otherImpactIndicator: string;
  prerequisites: string[];

  // Additional resources
  learnMoreLinks: UserUseCaseLearnMoreLink[];
}

const INITIAL_FORM_DATA: UseCaseFormData = {
  title: "",
  shortDescription: "",
  toolsUsed: [],
  categories: [],
  beforeWorkflow: "",
  afterWorkflow: "",
  steps: [{ order: 1, title: "", expectedResult: "", codeSnippet: "", prompt: "", learning: "" }],
  targetAudience: [],
  difficultyLevel: "",
  impactIndicators: [],
  otherImpactIndicator: "",
  prerequisites: [""],
  learnMoreLinks: [{ title: "", url: "" }],
};

const UseCaseContributePage: FC = () => {
  const history = useHistory();
  const [currentStep, setCurrentStep] = useState(0);
  const [draftId, setDraftId] = useState<string | null>(null);
  const [savedItem, setSavedItem] = useState<Record<string, any> | null>(null);
  const { addErrorBanner, addMessageBanner } = useMessageBannersBox();
  const [formData, setFormData] = useState<UseCaseFormData>(INITIAL_FORM_DATA);

  const updateFormData = (data: Partial<UseCaseFormData>) => {
    setFormData((prev) => ({ ...prev, ...data }));
  };

  const handleNext = () => {
    if (currentStep < CONTRIBUTE_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };

  const catalogicalService = useMemo(() => new CatalogicalService<any>("userusecase"), []);
  const csrfService = useMemo(() => new CSRFService(), []);

  const handleSaveDraft = async () => {
    const contributorAlias = UserPermissions.current.username || "unknown";
    const userInfo = UserInformation.current.getCachedAmazonianInformation();
    const contributorFullName = userInfo
      ? `${userInfo.basicInfo?.firstName || ""} ${userInfo.basicInfo?.lastName || ""}`.trim() || contributorAlias
      : contributorAlias;

    const payload = buildUseCasePayload(formData, "DRAFT", contributorAlias, contributorFullName);

    try {
      const csrfToken = await csrfService.getToken();
      let result;
      if (draftId && savedItem) {
        // Merge current form data into the saved Catalogical item, preserving catalogicalGenerated
        const updatedItem = { ...savedItem, ...payload };
        result = await catalogicalService.updateUserUseCase(draftId, updatedItem, csrfToken);
      } else {
        result = await catalogicalService.submitUserUseCase(payload, csrfToken);
      }
      if (result && "errorMessage" in result) {
        addErrorBanner(result.errorMessage || "Unable to save draft.", false);
        return;
      }
      const item = (result as any).item;
      setSavedItem(item);
      setDraftId(item?.taxonomyId || draftId);
      addMessageBanner("Draft saved successfully.", MessageBannerType.Success, 5000);
    } catch {
      addErrorBanner("Unable to save draft. Please try again.", false);
    }
  };

  const getStepStatus = (index: number): ProgressTrackerStepStatus => {
    if (index < currentStep) return ProgressTrackerStepStatus.Complete;
    if (index === currentStep) return ProgressTrackerStepStatus.Current;
    return ProgressTrackerStepStatus.Unavailable;
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <BasicInformationStep
            formData={formData}
            onChange={updateFormData}
            onNext={handleNext}
            onSaveDraft={handleSaveDraft}
          />
        );
      case 1:
        return (
          <WorkflowStep
            formData={formData}
            onChange={updateFormData}
            onNext={handleNext}
            onBack={handleBack}
            onSaveDraft={handleSaveDraft}
          />
        );
      case 2:
        return (
          <AudienceAndImpactStep
            formData={formData}
            onChange={updateFormData}
            onNext={handleNext}
            onBack={handleBack}
            onSaveDraft={handleSaveDraft}
          />
        );
      case 3:
        return (
          <AdditionalResourcesStep
            formData={formData}
            onChange={updateFormData}
            onNext={handleNext}
            onBack={handleBack}
            onSaveDraft={handleSaveDraft}
          />
        );
      default:
        return null;
    }
  };

  const handleReset = () => {
    setFormData(INITIAL_FORM_DATA);
    setCurrentStep(0);
    window.scrollTo(0, 0);
  };

  // Review step renders as a full standalone page (same as detail page)
  if (currentStep === CONTRIBUTE_STEPS.length - 1) {
    return (
      <ReviewAndSubmitStep
        formData={formData}
        onBack={handleBack}
        onSubmitted={handleReset}
        draftId={draftId}
        savedItem={savedItem}
        onDraftSaved={(id, item) => {
          setDraftId(id);
          setSavedItem(item);
        }}
      />
    );
  }

  return (
    <div className="contribute-use-case-page">
      <PageMainContainer>
        <Row justifyContent="space-between" alignItems="center">
          <Breadcrumbs
            crumbs={[
              { title: getString("logoText"), url: "/" },
              { title: getString("aiForMe.title"), url: "/aiforme" },
              { title: getString("useCases.browsePage.breadcrumbUseCases"), url: "/aiforme/usecases" },
              { title: getString("useCases.contributeForm.breadcrumbContribute") },
            ]}
            onCrumbClick={(e, crumb: CrumbProps) => {
              if (crumb.url) {
                e.preventDefault();
                history.push(crumb.url);
              }
            }}
          />
          <Button
            variant={ButtonVariant.Tertiary}
            icon={<IconCross />}
            aria-label="Cancel"
            touchpoint="use-case-contribute-cancel"
            onClick={() => history.goBack()}
          />
        </Row>

        <Col className="contribute-use-case-page__progress" padding={{ top: "S400", bottom: "S400" }}>
          <ProgressTracker
            selectedStep={currentStep}
            onStepSelected={(step) => {
              if (step < currentStep) setCurrentStep(step);
            }}
            aria-label="Use case contribution progress"
          >
            {CONTRIBUTE_STEPS.map((label, index) => (
              <ProgressTrackerStep key={label} labelText={label} status={getStepStatus(index)} />
            ))}
          </ProgressTracker>
        </Col>
      </PageMainContainer>

      <div className="contribute-use-case-page__content">{renderCurrentStep()}</div>
    </div>
  );
};

export default UseCaseContributePage;
